import React, { useState, useEffect } from 'react';
import { useUser } from '../UserContext';
import { BASE_URL } from '../conf/config';
import { Table, Tag, Collapse, Typography, Input, Select, Space } from 'antd';

const { Text } = Typography;
const { Panel } = Collapse;

function parseOrders(raw) {
    if (!raw) return { summary: '', replay: null };
    const parts = raw.split('---REPLAY---');
    const summary = parts[0].trim();
    let replay = null;
    if (parts[1]) {
        try { replay = JSON.stringify(JSON.parse(parts[1].trim()), null, 2); }
        catch { replay = parts[1].trim(); }
    }
    return { summary, replay };
}

const STATUS_COLOR = { success: 'green', fail: 'red', warning: 'orange' };

const expandedRow = (log) => {
    const { summary, replay } = parseOrders(log.orders);
    const items = [];
    if (summary) items.push({ key: 'summary', label: 'Orders / Summary', children: <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{summary}</pre> });
    if (log.warnings) items.push({ key: 'warnings', label: 'Details', children: <pre style={{ margin: 0, whiteSpace: 'pre-wrap', color: '#faad14' }}>{log.warnings}</pre> });
    if (log.errors) items.push({ key: 'errors', label: 'Errors', children: <pre style={{ margin: 0, whiteSpace: 'pre-wrap', color: '#ff4d4f' }}>{log.errors}</pre> });
    if (replay) items.push({ key: 'replay', label: 'Replay JSON', children: <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 12, color: '#888' }}>{replay}</pre> });

    if (items.length === 0) return <Text type="secondary">No details.</Text>;

    return (
        <Collapse size="small" defaultActiveKey={[]} items={items} />
    );
};

const COLUMNS = [
    {
        title: 'Timestamp',
        dataIndex: 'timestamp',
        key: 'timestamp',
        width: 170,
        render: ts => new Date(ts).toLocaleString(),
        sorter: (a, b) => new Date(a.timestamp) - new Date(b.timestamp),
        defaultSortOrder: 'descend',
    },
    {
        title: 'User',
        dataIndex: 'user',
        key: 'user',
        width: 100,
    },
    {
        title: 'Action',
        dataIndex: 'action',
        key: 'action',
        width: 100,
        filters: [],  // populated dynamically
        onFilter: (value, record) => record.action === value,
    },
    {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 90,
        render: s => <Tag color={STATUS_COLOR[s] || 'default'}>{s}</Tag>,
        filters: [
            { text: 'Success', value: 'success' },
            { text: 'Fail', value: 'fail' },
            { text: 'Warning', value: 'warning' },
        ],
        onFilter: (value, record) => record.status === value,
    },
    {
        title: 'Summary',
        key: 'summary',
        ellipsis: true,
        render: log => {
            const { summary } = parseOrders(log.orders);
            const firstLine = summary.split('\n')[0];
            return <Text type="secondary" style={{ fontSize: 13 }}>{firstLine || '—'}</Text>;
        },
    },
];

const LogsPage = () => {
    const { fetchWithAuth } = useUser();
    const [logs, setLogs] = useState([]);
    const [search, setSearch] = useState('');

    useEffect(() => {
        fetchWithAuth(`${BASE_URL}/api/log/`, { method: 'GET' })
            .then(res => res.json())
            .then(data => setLogs(data.map((l, i) => ({ ...l, key: i }))))
            .catch(err => console.error('Failed to fetch logs:', err));
    }, []);

    const actions = [...new Set(logs.map(l => l.action))].map(a => ({ text: a, value: a }));
    const columns = COLUMNS.map(c => c.key === 'action' ? { ...c, filters: actions } : c);

    const filtered = logs
        .filter(l => !search || [l.user, l.action, l.status, l.orders, l.warnings, l.errors]
            .join(' ').toLowerCase().includes(search.toLowerCase()));

    return (
        <div style={{ padding: '20px' }}>
            <Space style={{ marginBottom: 16 }}>
                <span data-help-id="log-search">
                <Input.Search
                    placeholder="Search logs..."
                    allowClear
                    style={{ width: 260 }}
                    onSearch={setSearch}
                    onChange={e => !e.target.value && setSearch('')}
                />
                </span>
            </Space>
            <div data-help-id="log-table">
            <Table
                columns={columns}
                dataSource={filtered}
                size="small"
                expandable={{
                    expandedRowRender: expandedRow,
                    defaultExpandAllRows: false,
                }}
                pagination={{ pageSize: 50, showSizeChanger: true }}
            />
            </div>
        </div>
    );
};

export default LogsPage;
